<script>
import { mapActions } from "vuex";
import ChooseLocale from "../../components/ChooseLocale.vue";
import ThemeToggle from "../../components/ThemeToggle.vue";
import { applyTheme, onThemeChange, saveTheme, storedDark } from "../../utils/theme";

export default {
  name: "Login",

  components: { ChooseLocale, ThemeToggle },

  data: () => ({
    login: {
      username: "",
      password: null,
    },

    isShow: false,
    isLoading: false,
    message: null,
    isDark: storedDark(),
    phoneFocused: false,
    passwordFocused: false,
  }),

  created() {
    const phone = this.$route.query.phone;
    if (Number(phone)) {
      let digits = `${phone}`.replace(/\D/g, "");
      if (digits.startsWith("998")) digits = digits.slice(3);
      this.login.username = digits.length ? "998" + digits : "";
      this.phoneDisplay = digits;
    }
  },

  mounted() {
    // The page paints itself from its own --bg tokens, but the language dropdown is a Vuetify
    // menu rendered at the app root, where only Vuetify's theme reaches it. Keep the two in step
    // so the list is dark on the dark page instead of a white card.
    applyTheme(this, this.isDark);
    this._stopThemeSync = onThemeChange((dark) => {
      this.isDark = dark;
      applyTheme(this, dark);
    });

    if (this.phoneDisplay) {
      this.$nextTick(() => {
        if (this.$refs.phone) {
          let d = this.phoneDisplay;
          let display = "";
          if (d.length > 0) display += d.slice(0, 2);
          if (d.length > 2) display += " " + d.slice(2, 5);
          if (d.length > 5) display += "-" + d.slice(5, 7);
          if (d.length > 7) display += "-" + d.slice(7, 9);
          this.$refs.phone.value = display;
        }
        this.$refs.password?.focus();
      });
    }
  },

  beforeDestroy() {
    if (this._stopThemeSync) this._stopThemeSync();
  },

  beforeRouteLeave(to, from, next) {
    next();
  },

  methods: {
    ...mapActions("auth", ["LOGIN"]),

    toggleTheme() {
      this.isDark = !this.isDark;
      saveTheme(this.isDark);
      applyTheme(this, this.isDark);
    },

    onSend() {
      if (!this.login.username || !this.login.password) return;
      const login = { ...this.login };
      this.isLoading = true;
      this.message = null;
      this.LOGIN(login)
        .then((msg) => {
          if (msg != true) {
            this.message = msg;
          }
        })
        .finally(() => (this.isLoading = false));
    },

    onSubmit() {
      this.onSend();
      return false;
    },

    formatPhone(e) {
      // Strip everything except digits, remove leading 998 if pasted
      let digits = e.target.value.replace(/\D/g, "");
      if (digits.startsWith("998")) digits = digits.slice(3);
      digits = digits.slice(0, 9);

      // Format display: "91 990-01-82"
      let display = "";
      if (digits.length > 0) display += digits.slice(0, 2);
      if (digits.length > 2) display += " " + digits.slice(2, 5);
      if (digits.length > 5) display += "-" + digits.slice(5, 7);
      if (digits.length > 7) display += "-" + digits.slice(7, 9);

      // Store full number for API
      this.login.username = digits.length ? "998" + digits : "";
      this.$nextTick(() => { e.target.value = display; });
    },
  },
};
</script>

<template>
  <main class="lp" :class="{ 'lp--dark': isDark }">
    <!-- Left panel -->
    <div class="lp-panel">
      <div class="lp-panel-overlay"></div>
      <div class="lp-panel-content">
        <!-- USAT Admin is the product's name, so it reads the same in every language. -->
        <div class="lp-badge">USAT Admin</div>
        <h2 class="lp-panel-title">{{ $t("University of Science and Technology") }}</h2>
        <p class="lp-panel-desc">{{ $t("Welcome to the management system") }}</p>
      </div>
    </div>

    <!-- Right -->
    <div class="lp-right">
      <div class="lp-topbar">
        <img src="/icons/logo-full-dark.svg" alt="USAT" class="lp-logo" />
        <div style="flex:1"></div>
        <choose-locale />
        <theme-toggle :dark="isDark" @toggle="toggleTheme" />
      </div>

      <div class="lp-form-area">
        <div class="lp-card">
          <h1 class="lp-title">{{ $t("Sign in") }}</h1>

          <form @submit.prevent="onSubmit" class="lp-form" autocomplete="off">
            <!-- Phone -->
            <div class="lp-field">
              <label class="lp-label">{{ $t("Phone number") }}</label>
              <div class="lp-input-wrap" :class="{ 'lp-input-wrap--focus': phoneFocused }">
                <span class="lp-input-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </span>
                <span class="lp-input-prefix">+998</span>
                <input
                  ref="phone"
                  type="tel"
                  class="lp-input"
                  @input="formatPhone"
                  @focus="phoneFocused = true"
                  @blur="phoneFocused = false"
                  @keyup.enter="$refs.password && $refs.password.focus()"
                  placeholder="00 000-00-00"
                  autofocus
                />
              </div>
            </div>

            <!-- Password -->
            <div class="lp-field">
              <label class="lp-label">{{ $t("Password") }}</label>
              <div class="lp-input-wrap" :class="{ 'lp-input-wrap--focus': passwordFocused }">
                <span class="lp-input-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </span>
                <input
                  ref="password"
                  :type="isShow ? 'text' : 'password'"
                  class="lp-input"
                  v-model="login.password"
                  :placeholder="$t('Enter password')"
                  @keyup.enter="onSend"
                  @focus="passwordFocused = true; message = null"
                  @blur="passwordFocused = false"
                />
                <button type="button" class="lp-eye" @click="isShow = !isShow" tabindex="-1">
                  <svg v-if="!isShow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Error -->
            <div v-if="message" class="lp-error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span>{{ message }}</span>
            </div>

            <!-- Submit -->
            <button type="submit" class="lp-btn" :disabled="isLoading">
              <span class="lp-btn-content">
                <span v-if="isLoading" class="lp-btn-spinner"></span>
                <span>{{ $t("Enter") }}</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                  <polyline points="12 5 19 12 12 19"/>
                </svg>
              </span>
            </button>
          </form>
        </div>

      </div>
    </div>
  </main>
</template>

<style lang="scss" scoped>
/* Brand display font, same files usat.uz serves (public/fonts/made) */
@font-face {
  font-family: "Made";
  src: url("/fonts/made/Made.otf") format("opentype");
  font-weight: 100 900; /* single-weight face: stops the browser faux-bolding it */
  font-style: normal;
  font-display: swap;
}

/* ─── Reset ─── */
.lp, .lp * {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Made", -apple-system, BlinkMacSystemFont, "SF Pro Display",
    "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif;
}

/* ═══ Light tokens ═══ */
.lp {
  --bg: #fafafa;
  --card-bg: #ffffff;
  --input-bg: #ffffff;
  --input-border: #e5e7eb;
  --input-hover: #d1d5db;
  --input-focus-border: #00274c;
  --input-ring: rgba(0, 0, 0, 0.06);
  --text: #111111;
  --text-2: #6b7280;
  --text-3: #9ca3af;
  --label: #374151;
  --icon-bg: #f5f5f5;
  --icon-border: #e5e5e5;
  --icon-color: #111111;
  --err: #ef4444;
  --err-bg: #fef2f2;
  --err-border: #fecaca;
  --footer: #b0b0b0;
  --logo-filter: none;
  --toggle-bg: #f2f2f2;
  --toggle-hover: #e8e8e8;
  --caret: #111111;
  --separator: #eaeaea;

  display: flex;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: var(--bg);
  -webkit-font-smoothing: antialiased;
}

/* ═══ Dark tokens ═══ */
.lp--dark {
  --bg: #0a0a0a;
  --card-bg: #0a0a0a;
  --input-bg: #111111;
  --input-border: #2a2a2a;
  --input-hover: #3a3a3a;
  --input-focus-border: #ffd451;
  --input-ring: rgba(255, 212, 81, 0.12);
  --text: #ededed;
  --text-2: #8b8b8b;
  --text-3: #555555;
  --label: #a1a1a1;
  --icon-bg: #1a1a1a;
  --icon-border: #2e2e2e;
  --icon-color: #ffd451;
  --err: #f87171;
  --err-bg: #1c1111;
  --err-border: #3d1418;
  --footer: #555555;
  --logo-filter: brightness(0) invert(1);
  --toggle-bg: #1a1a1a;
  --toggle-hover: #252525;
  --caret: #ffd451;
  --separator: #2e2e2e;
}

/* ═══ Left Panel ═══ */
.lp-panel {
  position: relative;
  width: 50%;
  min-height: 100vh;
  background: url("/images/auth-bg.webp") center / cover no-repeat;
  background-color: #00274c;
  border-radius: 0 32px 32px 0;
  overflow: hidden;
}

.lp-panel-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0,39,76,.3) 0%, rgba(0,20,45,.75) 100%);
}

.lp-panel-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 48px 40px;
  z-index: 1;
}

.lp-badge {
  display: inline-block;
  padding: 5px 14px;
  background: rgba(255,212,81,.15);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255,212,81,.3);
  border-radius: 20px;
  font-size: 12px;
  font-family: "Made", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif;
  font-weight: 400;
  color: #ffd451;
  letter-spacing: .5px;
  text-transform: uppercase;
  margin-bottom: 16px;
}

.lp-panel-title {
  font-family: "Made", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif;
  font-size: 28px;
  font-weight: 400;
  color: #fff;
  line-height: 1.25;
  margin-bottom: 8px;
  letter-spacing: -.3px;
}

.lp-panel-desc {
  font-size: 15px;
  color: rgba(255,255,255,.7);
  line-height: 1.5;
}

/* ═══ Right Side ═══ */
.lp-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--bg);
  transition: background .25s ease;
}

.lp-topbar {
  display: flex;
  align-items: center;
  padding: 28px 40px;
}

.lp-logo {
  height: 36px;
  object-fit: contain;
  filter: var(--logo-filter);
  transition: filter .25s ease;
}

/* The language control is the admin header's ChooseLocale, outlined here to sit on the plain
   form side. Vuetify's v-menu leaves an empty div where the component sits and renders its
   trigger as a sibling, so the button is reached through the topbar rather than through a
   class on the component. */
.lp-topbar ::v-deep .locale-trigger {
  height: 32px;
  margin-right: 10px;
  padding: 0 10px;
  border: 1.5px solid #00274d;
  border-radius: 8px;
  background: transparent;
  color: var(--text);
}
.lp-topbar ::v-deep .locale-trigger:hover {
  background: rgba(0, 39, 77, 0.06);
  border-color: #00274d;
  box-shadow: none;
}

.lp--dark .lp-topbar ::v-deep .locale-trigger {
  border-color: #4a4d5e;
  background: transparent;
  color: var(--text);
}
.lp--dark .lp-topbar ::v-deep .locale-trigger:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: #4a4d5e;
  box-shadow: none;
}

/* ═══ Form Area ═══ */
.lp-form-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 40px 32px;
}

.lp-card {
  width: 100%;
  max-width: 400px;
  animation: fadeUp .5s ease-out;
}

.lp-title {
  font-family: "Made", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif;
  font-size: 24px;
  font-weight: 400;
  color: var(--text);
  margin-bottom: 29px;
  letter-spacing: -.5px;
  transition: color .25s;
}

.lp-desc {
  font-size: 14px;
  color: var(--text-2);
  margin-bottom: 28px;
  transition: color .25s;
}

.lp-form { width: 100%; }

/* ═══ Fields ═══ */
.lp-field { margin-bottom: 20px; }

.lp-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--text);
  margin-bottom: 8px;
  transition: color .25s;
}

.lp-input-wrap {
  display: flex;
  align-items: center;
  height: 52px;
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  border-radius: 12px;
  overflow: hidden;
  transition: border-color .2s ease, box-shadow .2s ease;

  &:hover {
    border-color: var(--input-hover);
  }

  &--focus {
    border-color: var(--input-focus-border);
    border-width: 2px;
  }
}

.lp-input-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 16px;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    color: var(--text-3);
    transition: color .2s;
  }
}

.lp-input-wrap--focus .lp-input-icon svg {
  color: var(--text-2);
}

.lp-input-prefix {
  display: flex;
  align-items: center;
  padding: 0 2px 0 10px;
  flex-shrink: 0;
  font-size: 15px;
  font-weight: 500;
  color: var(--text-2);
  user-select: none;
  transition: color .2s;
}

.lp-input {
  flex: 1;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-size: 15px;
  font-weight: 400;
  color: var(--text);
  padding: 0 16px;
  caret-color: var(--caret);
  transition: color .15s;

  &::placeholder {
    color: var(--text-3);
    font-weight: 400;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 30px var(--input-bg) inset !important;
    -webkit-text-fill-color: var(--text) !important;
    transition: background-color 5000s ease-in-out 0s;
  }
}

.lp-eye {
  width: 48px;
  height: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    color: var(--text-3);
    transition: color .2s;
  }

  &:hover svg { color: var(--text-2); }
}

/* ═══ Error ═══ */
.lp-error {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 16px;
  font-size: 13px;
  line-height: 1.45;
  color: var(--err);
  background: var(--err-bg);
  border: 1px solid var(--err-border);
  border-radius: 8px;
  transition: background .25s, border-color .25s, color .25s;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    margin-top: 1px;
    color: var(--err);
  }
}

/* ═══ Submit Button ═══ */
.lp-btn {
  position: relative;
  width: 100%;
  height: 50px;
  border: 2px solid #e6b800;
  border-radius: 14px;
  background: #ffd451;
  cursor: pointer;
  overflow: visible;
  margin-top: 12px;
  outline: none;
  transition: transform .12s ease, border-color .2s ease;
  isolation: isolate;

  &:hover,
  &:focus-visible {
    transform: translateY(-1px);
    border-color: #ccaa00;
  }

  &:active {
    transform: translateY(0) scale(.98);
    border-color: #ccaa00;
  }

  &:disabled {
    opacity: .5;
    cursor: not-allowed;
    transform: none;

    &::before, &::after { display: none; }
  }
}

.lp--dark .lp-btn {
  border-color: #b8960a;
}

/* Kill Vuetify ripple on button */
.lp-btn ::v-deep .v-ripple__container {
  display: none !important;
}

.lp-btn-content {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: .5px;
  text-transform: uppercase;
  color: #1a1400;
}

.lp-btn-spinner {
  width: 16px;
  height: 16px;
  border: 2.5px solid rgba(26, 20, 0, .2);
  border-top-color: #1a1400;
  border-radius: 50%;
  animation: spin .6s linear infinite;
}

/* ═══ Keyframes ═══ */
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ═══ Responsive ═══ */
@media (max-width: 1100px) {
  .lp-panel { width: 42%; }
}

@media (max-width: 860px) {
  .lp {
    flex-direction: column;
    height: auto;
    min-height: 100vh;
  }
  .lp-panel { display: none; }
  .lp-right { min-height: 100vh; }
  .lp-topbar { padding: 20px 20px 12px; }
  .lp-logo { height: 22px; }
  .lp-form-area { padding: 0 20px 32px; }
  .lp-card { max-width: 100%; }
}
</style>
